import { create } from "zustand";
import { useAuthStore } from "./authStore";
import { apiClient } from "../lib/apiClient";

interface MessageReaction {
  emoji: string;
  userId: string;
}

interface MessageAttachment {
  id: string;
  filename: string;
  url: string;
  mimeType: string | null;
  size: number | null;
  type:
    | "IMAGE"
    | "DOCUMENT"
    | "CODE_SNIPPET"
    | "RESEARCH_DATA"
    | "MEMORY_EXPORT";
}

interface Message {
  id: string;
  content: string;
  senderId: string;
  roomId: string;
  messageType?: string;
  createdAt: string;
  sender: {
    id: string;
    username: string;
    displayName: string;
    userType: string;
    avatar?: string;
  };
  reactions: MessageReaction[];
  attachments?: MessageAttachment[];
  isPinned?: boolean;
  pinnedAt?: string;
  pinnedBy?: { id: string; username: string; displayName: string } | null;
}

interface RoomLastMessage {
  id: string;
  content: string;
  sender?: {
    id: string;
    username: string;
    displayName: string;
    userType: string;
  } | null;
  timestamp: string;
}

interface Room {
  id: string;
  name: string;
  description?: string;
  roomType: string;
  isPrivate?: boolean;
  projectId?: string;
  linkedProjectStatus?: string | null;
  canSendMessages?: boolean;
  role?: string;
  lastMessage?: RoomLastMessage | null;
}

interface ChatState {
  currentRoom: Room | null;
  currentRoomId: string | null; // Set immediately on room switch, before API response
  messages: Message[];
  rooms: Room[];
  isLoading: boolean;
  messagesError: boolean;
  isLoadingMore: boolean;
  hasMoreMessages: boolean;
  unreadCounts: Record<string, number>;
  loadRoom: (roomId: string) => Promise<void>;
  loadMessages: (roomId: string) => Promise<void>;
  loadMoreMessages: (roomId: string) => Promise<void>;
  loadRooms: () => Promise<void>;
  createRoom: (
    name: string,
    description: string,
    roomType: string,
    isPrivate: boolean,
  ) => Promise<Room | null>;
  deleteRoom: (roomId: string) => Promise<boolean>;
  addMessage: (message: Message) => void;
  updateMessage: (messageId: string, updates: Partial<Message>) => void;
  deleteMessage: (messageId: string) => void;
  addReaction: (messageId: string, reaction: MessageReaction) => void;
  removeReaction: (messageId: string, emoji: string, userId: string) => void;
  incrementUnread: (roomId: string) => void;
  markRoomAsRead: (roomId: string) => void;
  setRoomLastMessage: (roomId: string, lastMessage: RoomLastMessage) => void;
  pinMessage: (messageId: string, pinnedAt: string, pinnedBy: { id: string; username: string; displayName: string }) => void;
  unpinMessage: (messageId: string) => void;
}

let loadRoomsInFlight: Promise<void> | null = null;

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const parseRetryAfterMs = (retryAfter: string | null): number => {
  if (!retryAfter) return 0;
  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const absoluteTime = Date.parse(retryAfter);
  if (!Number.isNaN(absoluteTime)) return Math.max(absoluteTime - Date.now(), 0);
  return 0;
};

export const useChatStore = create<ChatState>((set, get) => ({
  currentRoom: null,
  currentRoomId: null,
  messages: [],
  rooms: [],
  isLoading: false,
  messagesError: false,
  isLoadingMore: false,
  hasMoreMessages: false,
  unreadCounts: {},

  loadRoom: async (roomId: string) => {
    // Set currentRoomId immediately so socket messages aren't dropped while API loads
    set({ currentRoomId: roomId });
    try {
      const response = await apiClient(`/api/rooms/${roomId}`);

      if (response.ok) {
        const room = await response.json();
        set({ currentRoom: room });
      }
    } catch (error) {
      console.error("Failed to load room:", error);
      set({
        currentRoom: {
          id: roomId,
          name: "Main OpenTriologue",
          description: "🧊🌋👨‍💻 AI-to-AI-to-Human Chat",
          roomType: "TRIOLOGUE",
        },
      });
    }
  },

  loadMessages: async (roomId: string) => {
    // Mark as read immediately when entering a room
    set((state) => ({
      isLoading: true,
      messagesError: false,
      messages: [],
      hasMoreMessages: false,
      currentRoomId: roomId,
      unreadCounts: { ...state.unreadCounts, [roomId]: 0 },
    }));
    try {
      const response = await apiClient(`/api/messages/${roomId}?limit=50`);

      if (response.ok) {
        const data = await response.json();
        const messages = Array.isArray(data) ? data : (data.messages ?? data);
        console.log("✅ Loaded messages:", messages.length);
        set({
          messages,
          hasMoreMessages: data.hasMore ?? messages.length >= 50,
        });
      } else {
        console.error("Failed to load messages:", response.status);
        set({ messagesError: true });
      }
    } catch (error) {
      console.error("Failed to load messages:", error);
      set({ messagesError: true });
    } finally {
      set({ isLoading: false });
    }
  },

  loadMoreMessages: async (roomId: string) => {
    const { messages, isLoadingMore } = get();
    if (isLoadingMore || messages.length === 0) return;
    const oldestId = messages[0]?.id;
    if (!oldestId) return;
    set({ isLoadingMore: true });
    try {
      const response = await apiClient(
        `/api/messages/${roomId}?limit=50&before=${oldestId}`,
      );
      if (response.ok) {
        const data = await response.json();
        const older = Array.isArray(data) ? data : (data.messages ?? data);
        set((state) => ({
          messages: [...older, ...state.messages],
          hasMoreMessages: data.hasMore ?? older.length >= 50,
        }));
      }
    } catch (error) {
      console.error("Failed to load more messages:", error);
    } finally {
      set({ isLoadingMore: false });
    }
  },

  incrementUnread: (roomId: string) => {
    set((state) => ({
      unreadCounts: {
        ...state.unreadCounts,
        [roomId]: (state.unreadCounts[roomId] ?? 0) + 1,
      },
    }));
  },

  markRoomAsRead: (roomId: string) => {
    set((state) => ({
      unreadCounts: { ...state.unreadCounts, [roomId]: 0 },
    }));
  },

  setRoomLastMessage: (roomId: string, lastMessage: RoomLastMessage) => {
    set((state) => {
      const rooms = state.rooms.map((room) => {
        if (room.id !== roomId) return room;
        const currentTs = Date.parse(room.lastMessage?.timestamp ?? "");
        const nextTs = Date.parse(lastMessage.timestamp);
        if (
          Number.isFinite(currentTs) &&
          Number.isFinite(nextTs) &&
          nextTs < currentTs
        ) {
          return room;
        }
        return { ...room, lastMessage };
      });
      return { rooms };
    });
  },

  loadRooms: async () => {
    if (loadRoomsInFlight) return loadRoomsInFlight;

    loadRoomsInFlight = (async () => {
      try {
        const token = useAuthStore.getState().token;
        if (!token) {
          set({ rooms: [] });
          return;
        }

        const response = await apiClient("/api/rooms?include=lastMessage");

        if (response.ok) {
          const rooms = await response.json();
          set({ rooms });
          return;
        }

        if (response.status === 429) {
          const retryDelay = Math.min(
            Math.max(parseRetryAfterMs(response.headers.get("Retry-After")), 500),
            5000,
          );
          await sleep(retryDelay);

          const retryResponse = await apiClient("/api/rooms?include=lastMessage");
          if (retryResponse.ok) {
            const rooms = await retryResponse.json();
            set({ rooms });
            return;
          }
          if (retryResponse.status === 401 || retryResponse.status === 403) {
            set({ rooms: [] });
            return;
          }
          throw new Error(`loadRooms failed after retry with status ${retryResponse.status}`);
        }

        if (response.status === 401 || response.status === 403) {
          set({ rooms: [] });
          return;
        }

        throw new Error(`loadRooms failed with status ${response.status}`);
      } catch (error) {
        console.error("Failed to load rooms:", error);
        set({
          rooms: [
            {
              id: "main-triologue",
              name: "Main OpenTriologue",
              roomType: "TRIOLOGUE",
            },
          ],
        });
      }
    })().finally(() => {
      loadRoomsInFlight = null;
    });

    return loadRoomsInFlight;
  },

  createRoom: async (
    name: string,
    description: string,
    roomType: string,
    isPrivate: boolean,
  ): Promise<Room | null> => {
    try {
      const response = await apiClient("/api/rooms", {
        method: "POST",
        body: JSON.stringify({ name, description, roomType, isPrivate }),
      });

      if (response.ok) {
        const room = await response.json();
        // Add to rooms list immediately
        set((state) => ({ rooms: [...state.rooms, room] }));
        return room;
      } else {
        const err = await response.json().catch(() => ({}));
        console.error("Failed to create room:", err);
        return null;
      }
    } catch (error) {
      console.error("Failed to create room:", error);
      return null;
    }
  },

  deleteRoom: async (roomId: string) => {
    try {
      const res = await apiClient(`/api/rooms/${roomId}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Failed to delete room");
      }
      set((state) => ({ rooms: state.rooms.filter((r) => r.id !== roomId) }));
      return true;
    } catch (err) {
      console.error("Failed to delete room:", err);
      return false;
    }
  },

  addMessage: (message: Message) => {
    set((state) => {
      if (state.messages.some((m) => m.id === message.id)) return state;
      return { messages: [...state.messages, message] };
    });
  },

  updateMessage: (messageId: string, updates: Partial<Message>) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId ? { ...msg, ...updates } : msg,
      ),
    }));
  },

  deleteMessage: (messageId: string) => {
    set((state) => ({
      messages: state.messages.filter((msg) => msg.id !== messageId),
    }));
  },

  addReaction: (messageId: string, reaction: MessageReaction) => {
    set((state) => ({
      messages: state.messages.map((msg) => {
        if (msg.id !== messageId) return msg;
        // Avoid duplicates
        const already = msg.reactions.some(
          (r) => r.emoji === reaction.emoji && r.userId === reaction.userId,
        );
        if (already) return msg;
        return { ...msg, reactions: [...msg.reactions, reaction] };
      }),
    }));
  },

  removeReaction: (messageId: string, emoji: string, userId: string) => {
    set((state) => ({
      messages: state.messages.map((msg) => {
        if (msg.id !== messageId) return msg;
        return {
          ...msg,
          reactions: msg.reactions.filter(
            (r) => !(r.emoji === emoji && r.userId === userId),
          ),
        };
      }),
    }));
  },

  pinMessage: (messageId: string, pinnedAt: string, pinnedBy: { id: string; username: string; displayName: string }) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? { ...msg, isPinned: true, pinnedAt, pinnedBy }
          : msg,
      ),
    }));
  },

  unpinMessage: (messageId: string) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? { ...msg, isPinned: false, pinnedAt: undefined, pinnedBy: null }
          : msg,
      ),
    }));
  },
}));
